import React from 'react';
import NumberFormat from 'react-number-format';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Label,
  Table,
  Alert,
} from 'reactstrap';
import { InputForm, HTMLForm } from '../../../CustomForms';
import CurrencyField from '../fields/CurrencyField';
import EarlyStartEndDatePicker from '../fields/EarlyStartEndDatePicker';

// [form field name, for= label, visible title, extras]
const SINGLE_CURRENCY_PRICES = [
  {
    name: 'price',
    label: 'Normal Price',
    title: 'Normal Price',
    showFeedback: true,
  },
  {
    name: 'normalPromoPrice',
    label: 'Normal Promo Price',
    title: 'Normal Promo Price',
  },
  { name: 'term2Price', label: 'Term 2 Price', title: 'Term 2 Price' },
  { name: 'termPrice', label: 'Term Price', title: 'Term Price' },
  { name: 'earlyPrice', label: 'Early Bird', title: 'Early Bird Price' },
  { name: 'specialPrice', label: 'Special Price', title: 'Special Price' },
  { name: 'corporatePrice', label: 'Corporate', title: 'Corporate Price' },
];

const PriceCard = ({
  fields,
  isMultipleCurrency,
  handleInput,
  handleCurrencyInput,
  onAddPriceClick,
  onEditPrice,
  onDeletePrice,
  promoDates,
}) => {
  const { openPickers } = promoDates;

  return (
    <Card className="my-4">
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <div>Price Details</div>
          <div>
            {isMultipleCurrency && (
              <Button size="sm" onClick={onAddPriceClick} className="mr-1">
                Add Price
              </Button>
            )}

            {/* NOTE: temporary hidden */}
            {/* <Button size="sm" onClick={onAddPromoClick}>
              Add Promo
            </Button> */}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <InputForm
          {...fields.priceTitle}
          label="Price Title"
          onInput={(value) => handleInput('priceTitle', value)}
        />
        <InputForm
          {...fields.priceTitleDesc}
          label="Price Title Description"
          onInput={(value) => handleInput('priceTitleDesc', value)}
        />
        <InputForm
          {...fields.priceQuota}
          label="Price Quota"
          onInput={(value) => handleInput('priceQuota', value)}
          type="number"
        />
        <InputForm
          {...fields.priceQuotaRemaining}
          label="Price Quota Remaining"
          onInput={(value) => handleInput('priceQuotaRemaining', value)}
          type="number"
        />
        <HTMLForm
          {...fields.priceInfo}
          label="Price Information"
          onInput={(value) => handleInput('priceInfo', value)}
        />
        <br />
        {isMultipleCurrency ? (
          <>
            <Label className="text-gray font-weight-bold">Currency</Label>
            <Table bordered>
              <thead>
                <tr>
                  <td>Currency</td>
                  <td>Normal Price</td>
                  <td>Early Bird Price</td>
                  <td>Registration Fee</td>
                  <td>Action</td>
                </tr>
              </thead>
              <tbody>
                {fields.programPrices.value.map((price, index) => {
                  const {
                    currency,
                    normalPrice,
                    earlyPrice,
                    registrationFee,
                  } = price;

                  const { id: currencyId, name, symbol } = currency || {};
                  return (
                    <tr key={currencyId}>
                      <th scope="row">{name}</th>
                      <td>
                        <NumberFormat
                          value={normalPrice}
                          displayType="text"
                          thousandSeparator
                          prefix={symbol}
                        />
                      </td>
                      <td>
                        <NumberFormat
                          value={earlyPrice}
                          displayType="text"
                          thousandSeparator
                          prefix={symbol}
                        />
                      </td>
                      <td>
                        <NumberFormat
                          value={registrationFee}
                          displayType="text"
                          thousandSeparator
                          prefix={symbol}
                        />
                      </td>
                      <td>
                        <div>
                          <Button
                            className="mr-2"
                            onClick={() => onEditPrice(price)}
                            size="sm"
                            type="button"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => onDeletePrice(index)}
                            size="sm"
                            type="button"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {fields.programPrices.invalid && (
              <Alert color="danger">Please input a minimum of one price</Alert>
            )}
          </>
        ) : (
          <>
            {SINGLE_CURRENCY_PRICES.map(
              ({ name, label, title, showFeedback }) => (
                <CurrencyField
                  key={name}
                  label={label}
                  title={title}
                  field={fields[name]}
                  showFeedback={showFeedback}
                  onChange={({ target }) =>
                    handleCurrencyInput(name, target.value)
                  }
                />
              ),
            )}
            <CurrencyField
              label="Registration Fee"
              title="Registration Fee"
              field={fields.registrationFee}
              disabled
              onChange={() => handleCurrencyInput('registrationFee', '0')}
            />
            <br />
          </>
        )}
        {/* NOTE: Temporary Hidden — promos table + special price inputs.
            Handlers and payload fields stay live; re-enable by wiring to
            fields.programAdditionalPromos / handleInput as elsewhere. */}
        <EarlyStartEndDatePicker
          handleSpecialStartDate={promoDates.handleSpecialStartDate}
          handleSpecialEndDate={promoDates.handleSpecialEndDate}
          datePickerSpecialStartDate={openPickers.datePickerSpecialStartDate}
          datePickerSpecialEndDate={openPickers.datePickerSpecialEndDate}
          specialStartDate={fields.specialStartDate}
          specialEndDate={fields.specialEndDate}
          handleEarlyStartDate={promoDates.handleEarlyStartDate}
          handleEarlyEndDate={promoDates.handleEarlyEndDate}
          datePickerEarlyStartDate={openPickers.datePickerEarlyStartDate}
          datePickerEarlyEndDate={openPickers.datePickerEarlyEndDate}
          earlyStartDate={fields.earlyStartDate}
          earlyEndDate={fields.earlyEndDate}
          handleTermStartDate={promoDates.handleTermStartDate}
          handleTermEndDate={promoDates.handleTermEndDate}
          handleTerm2StartDate={promoDates.handleTerm2StartDate}
          handleTerm2EndDate={promoDates.handleTerm2EndDate}
          datePickerTermStartDate={openPickers.datePickerTermStartDate}
          datePickerTermEndDate={openPickers.datePickerTermEndDate}
          datePickerTerm2StartDate={openPickers.datePickerTerm2StartDate}
          datePickerTerm2EndDate={openPickers.datePickerTerm2EndDate}
          termStartDate={fields.termStartDate}
          termEndDate={fields.termEndDate}
          term2StartDate={fields.term2StartDate}
          term2EndDate={fields.term2EndDate}
          handleNormalPromoStartDate={promoDates.handleNormalPromoStartDate}
          handleNormalPromoEndDate={promoDates.handleNormalPromoEndDate}
          datePickerNormalPromoStartDate={
            openPickers.datePickerNormalPromoStartDate
          }
          datePickerNormalPromoEndDate={
            openPickers.datePickerNormalPromoEndDate
          }
          normalPromoStartDate={fields.normalPromoStartDate}
          normalPromoEndDate={fields.normalPromoEndDate}
          handleCorporateSaleStartDate={promoDates.handleCorporateSaleStartDate}
          corporateSaleStartDate={fields.corporateSaleStartDate}
          datePickerCorporateSaleStartDate={
            openPickers.datePickerCorporateSaleStartDate
          }
          handleCorporateSaleEndDate={promoDates.handleCorporateSaleEndDate}
          corporateSaleEndDate={fields.corporateSaleEndDate}
          datePickerCorporateSaleEndDate={
            openPickers.datePickerCorporateSaleEndDate
          }
          handleSaleEndDate={promoDates.handleSaleEndDate}
          datePickerSaleEndDate={openPickers.datePickerSaleEndDate}
          saleEndDate={fields.saleEndDate}
          openDatePicker={promoDates.openDatePicker}
          closeDatePicker={promoDates.closeDatePicker}
        />
      </CardBody>
    </Card>
  );
};

export default PriceCard;
